import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FileText,
  Mail,
  MessageSquare,
  Inbox,
  BarChart3,
  GraduationCap,
  Package,
  Sparkles,
  RotateCcw,
  MessagesSquare,
  Phone,
  ExternalLink,
  LogOut,
  Receipt,
  History,
  Users,
} from "lucide-react";
import { adminLogout } from "@/lib/api";

const TABS = [
  { to: "/admin/blog", label: "Blog", icon: FileText, testid: "adminnav-blog" },
  { to: "/admin/newsletters", label: "Newsletters", icon: Mail, testid: "adminnav-newsletters" },
  { to: "/admin/comments", label: "Comments", icon: MessageSquare, testid: "adminnav-comments" },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox, testid: "adminnav-submissions" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "adminnav-analytics" },
  { to: "/admin/concierge", label: "Concierge", icon: MessagesSquare, testid: "adminnav-concierge" },
  { to: "/admin/voice", label: "Voice", icon: Phone, testid: "adminnav-voice" },
  { to: "/admin/learning-hub", label: "Learning Hub", icon: GraduationCap, testid: "adminnav-learning" },
  { to: "/admin/therabody", label: "Therabody", icon: Package, testid: "adminnav-therabody" },
  { to: "/admin/invoices", label: "Invoice Generator", icon: Receipt, testid: "adminnav-invoices" },
  { to: "/admin/invoice-history", label: "Invoice History", icon: History, testid: "adminnav-invoice-history" },
  { to: "/admin/invoice-clients", label: "Invoice Clients", icon: Users, testid: "adminnav-invoice-clients" },
  { to: "/admin/beta", label: "Beta Preview", icon: Sparkles, testid: "adminnav-beta" },
  { to: "/admin/refresh", label: "Refresh", icon: RotateCcw, testid: "adminnav-refresh" },
];

/**
 * Persistent admin top-nav. Rendered by AdminLayout on every `/admin/*` route
 * except `/admin/login`. Keeps all dashboards one click away from each other.
 */
export default function AdminNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const onLogout = async () => {
    try {
      await adminLogout();
    } catch {
      /* swallow — clear local state regardless */
    }
    navigate("/admin/login", { replace: true });
  };

  const isActive = (to) =>
    pathname === to || (to !== "/admin" && pathname.startsWith(to + "/"));

  return (
    <nav
      data-testid="admin-nav"
      className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center gap-2 h-14 overflow-x-auto whitespace-nowrap">
          <Link
            to="/admin/blog"
            data-testid="adminnav-home"
            className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#080808] mr-4 shrink-0 hover:text-[#3A7A8C] transition-colors"
          >
            Ashlan · Admin
          </Link>
          <div className="flex items-center gap-1 flex-1">
            {TABS.map(({ to, label, icon: Icon, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={[
                  "inline-flex items-center gap-1.5 px-3 py-2 text-sm transition-colors shrink-0",
                  isActive(to)
                    ? "text-[#3A7A8C] font-semibold border-b-2 border-[#5FA8B4] -mb-px"
                    : "text-[#3C4048] hover:text-[#080808]",
                ].join(" ")}
              >
                <Icon size={14} />
                {label}
              </Link>
            ))}
          </div>
          <a
            href="https://stats.uptimerobot.com/GivWo8aidQ"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="adminnav-status"
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-xs text-[#3C4048] hover:text-[#080808] shrink-0"
          >
            Status <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={onLogout}
            data-testid="adminnav-logout"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-[#3C4048] hover:text-[#080808] shrink-0"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>
    </nav>
  );
}
